/**
 * Offline Technique Data Hooks
 * 
 * Hooks for accessing technique-related data with offline support.
 * Includes techniques, user techniques, and positions with cache-first loading.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useOfflineState } from '../useOfflineState';
import { indexedDBService } from '@/services/offline/indexeddb';
import type {
  PositionOffline
} from '@/services/offline/indexeddb/schema';
import type { Technique, Position, UserTechnique } from '@/types/technique';
import type { DataFetchOptions, DataFetchResult } from './shared';
import { transformTechniqueOfflineToTechnique } from './shared';

/**
 * Hook for techniques with offline support
 * 
 * Fetches all techniques with automatic transformation from offline format to UI format.
 * Handles cache-first loading with intelligent sync strategies.
 * 
 * @param options - Fetch options including cache behavior
 * @returns All techniques data with loading states and refresh capability
 */
export function useOfflineTechniques(
  options: DataFetchOptions = {}
): DataFetchResult<Technique[]> {
  const { state } = useOfflineState();
  const [data, setData] = useState<Technique[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Use ref to track current data without causing re-renders
  const dataRef = useRef<Technique[] | null>(null);

  // Update ref when data changes
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const { forceRefresh = false } = options;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Always try cache first for offline-first approach
      const cachedData = await indexedDBService.techniques.getAll();

      if (cachedData.length > 0) {
        setData(cachedData.map(transformTechniqueOfflineToTechnique));
        setIsFromCache(true);
        setLastUpdated(new Date().toISOString());
        setLoading(false);

        // If offline or not forcing refresh, use cached data and stop here
        if (!state.isOnline || !forceRefresh) {
          return;
        }

        // If online and forcing refresh, continue to sync but we already have data
      } else if (!state.isOnline) {
        // If offline and no cache, return empty array
        setData([]);
        setLoading(false);
        return;
      }

      // Only sync if:
      // 1. We're online AND
      // 2. (We're forcing refresh OR we found no cached data)
      if (state.isOnline && (forceRefresh || cachedData.length === 0)) {
        try {
          // Import sync service dynamically to avoid circular dependencies
          const { syncService } = await import('@/services/offline');

          // Only sync if not already syncing to prevent loops
          if (!syncService.getStatus().isSyncing) {
            await syncService.performSync();
          }
          const updatedData = await indexedDBService.techniques.getAll();
          setData(updatedData.map(transformTechniqueOfflineToTechnique));
          setIsFromCache(false);
        } catch (syncError) {
          console.error('Sync failed:', syncError);
          // Continue with cached data if available
          if (cachedData.length === 0) {
            setData([]);
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch techniques';
      setError(errorMessage);

      // Fallback to cache
      try {
        const cachedData = await indexedDBService.techniques.getAll();
        setData(cachedData.map(transformTechniqueOfflineToTechnique));
        setIsFromCache(true);
      } catch {
        setData([]);
      }
    } finally {
      setLoading(false);
    }
  }, [state.isOnline, forceRefresh]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh
  };
}

/**
 * Hook for a single technique by ID with offline support
 *
 * @param techniqueId - The technique ID to fetch
 * @param options - Fetch options
 * @returns Single technique data with loading states
 */
export function useOfflineTechnique(
  techniqueId?: string,
  options: DataFetchOptions = {}
): DataFetchResult<Technique> {
  const { state } = useOfflineState();
  const [data, setData] = useState<Technique | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const { cacheFirst = true, forceRefresh = false } = options;

  const fetchData = useCallback(async () => {
    if (!techniqueId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Try cache first
      if (cacheFirst || !state.isOnline) {
        const cachedData = await indexedDBService.techniques.getById(techniqueId);
        
        if (cachedData) {
          setData(transformTechniqueOfflineToTechnique(cachedData));
          setIsFromCache(true);
          setLastUpdated(new Date().toISOString());

          if (!state.isOnline || !forceRefresh) {
            setLoading(false);
            return;
          }
        }
      }

      // If online and (no cache data or forcing refresh), sync from server
      if (state.isOnline) {
        try {
          const { syncService } = await import('@/services/offline');
          if (!syncService.getStatus().isSyncing) {
            await syncService.performSync();
          }
          const updatedData = await indexedDBService.techniques.getById(techniqueId);
          if (updatedData) {
            setData(transformTechniqueOfflineToTechnique(updatedData));
            setIsFromCache(false);
          }
        } catch (syncError) {
          console.error('Sync failed:', syncError);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch technique';
      setError(errorMessage);
      
      // Fallback to cache
      try {
        const cachedData = await indexedDBService.techniques.getById(techniqueId);
        if (cachedData) {
          setData(transformTechniqueOfflineToTechnique(cachedData));
          setIsFromCache(true);
        }
      } catch {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [techniqueId, state.isOnline, cacheFirst, forceRefresh]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh
  };
}

/**
 * Hook for a single technique by slug with offline support
 *
 * @param slug - The technique slug to fetch
 * @param options - Fetch options
 * @returns Single technique data with loading states
 */
export function useOfflineTechniqueBySlug(
  slug?: string,
  options: DataFetchOptions = {}
): DataFetchResult<Technique> {
  const { state } = useOfflineState();
  const [data, setData] = useState<Technique | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Use ref to track current data without causing re-renders
  const dataRef = useRef<Technique | null>(null);

  // Update ref when data changes
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const { cacheFirst = true, forceRefresh = false } = options;

  const fetchData = useCallback(async () => {
    if (!slug) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Try cache first
      if (cacheFirst || !state.isOnline) {
        const cachedData = await indexedDBService.techniques.getBySlug(slug);

        if (cachedData) {
          setData(transformTechniqueOfflineToTechnique(cachedData));
          setIsFromCache(true);
          setLastUpdated(new Date().toISOString());

          if (!state.isOnline || !forceRefresh) {
            setLoading(false);
            return;
          }
        }
      }

      // If online and no cache or forcing refresh, fetch from server
      // Use ref to avoid dependency loop
      if (state.isOnline && (forceRefresh || !dataRef.current)) {
        const { getTechniqueBySlug } = await import('@/services/technique/baseTechniqueService');
        const serverData = await getTechniqueBySlug(slug);

        if (serverData) {
          setData(serverData);
          setIsFromCache(false);
          setLastUpdated(new Date().toISOString());
        } else {
          setData(null);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch technique';
      setError(errorMessage);

      // Fallback to cache
      try {
        const cachedData = await indexedDBService.techniques.getBySlug(slug);
        if (cachedData) {
          setData(transformTechniqueOfflineToTechnique(cachedData));
          setIsFromCache(true);
        }
      } catch {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [slug, state.isOnline, cacheFirst, forceRefresh]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh
  };
}

/**
 * Hook for user techniques with offline support
 *
 * Fetches user's personal techniques with joins to technique and position data.
 * Handles complex data relationships and transformations for the UI.
 *
 * @param userId - The user ID to fetch techniques for
 * @param options - Fetch options including cache behavior
 * @returns User techniques with joined data and loading states
 */
export function useOfflineUserTechniques(
  userId?: string,
  options: DataFetchOptions = {}
): DataFetchResult<UserTechnique[]> {
  const { state } = useOfflineState();
  const [data, setData] = useState<UserTechnique[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const {
    cacheFirst = true,
    forceRefresh = false,
    maxAge = 5 * 60 * 1000 // 5 minutes
  } = options;

  const fetchData = useCallback(async () => {
    if (!userId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Always try cache first for offline-first approach
      if (cacheFirst || !state.isOnline) {
        const [userTechniques, techniques, positions] = await Promise.all([
          indexedDBService.userTechniques.getByUserId(userId),
          indexedDBService.techniques.getAll(),
          indexedDBService.positions.getAll()
        ]);

        if (userTechniques.length > 0) {
          // Join user techniques with technique and position data
          const joinedData: UserTechnique[] = userTechniques
            .map(userTech => {
              const technique = techniques.find(t => t.id === userTech.technique_id);
              const position = positions.find(p => p.id === technique?.position_id);

              return {
                userTech,
                technique,
                position
              };
            })
            .filter(({ technique }) => technique !== undefined) // Only include user techniques with valid techniques
            .map(({ userTech, technique }) => ({
              id: userTech.id,
              user_id: userTech.user_id,
              technique_id: userTech.technique_id,
              proficiency_level: userTech.proficiency_level || 'beginner',
              is_favorite: Boolean(userTech.is_favorite),
              notes: userTech.notes,
              last_practiced: userTech.last_practiced,
              created_at: userTech.created_at,
              technique: transformTechniqueOfflineToTechnique(technique!)
            }));

          setData(joinedData);
          setIsFromCache(true);
          setLastUpdated(new Date().toISOString());

          // If offline or cache is fresh enough, use cached data
          if (!state.isOnline || (!forceRefresh && Date.now() - new Date(state.dataLastUpdated || 0).getTime() < maxAge)) {
            setLoading(false);
            return;
          }
        }
      }

      // If online and (no cache data or cache is stale), fetch from server
      if (state.isOnline) {
        try {
          // For user techniques, we should NOT trigger sync on every page load
          // Sync should only happen during initial app load or manual refresh
          // Just get the current data from IndexedDB (which should be up-to-date from initial sync)

          const updatedUserTechniques = await indexedDBService.userTechniques.getByUserId(userId);
          const updatedJoinedData: UserTechnique[] = [];

          for (const userTech of updatedUserTechniques) {
            const technique = await indexedDBService.techniques.getById(userTech.technique_id);
            if (technique) { // Only process if technique exists
              updatedJoinedData.push({
                id: userTech.id,
                user_id: userTech.user_id,
                technique_id: userTech.technique_id,
                proficiency_level: userTech.proficiency_level || 'beginner',
                is_favorite: Boolean(userTech.is_favorite),
                notes: userTech.notes,
                last_practiced: userTech.last_practiced,
                created_at: userTech.created_at,
                technique: transformTechniqueOfflineToTechnique(technique)
              });
            }
          }

          setData(updatedJoinedData);
          setIsFromCache(false);
        } catch (syncError) {
          console.error('Failed to fetch updated user techniques:', syncError);
          // Continue with cached data if available
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user techniques';
      setError(errorMessage);

      // Try to get cached data as fallback
      try {
        const [userTechniques, techniques, positions] = await Promise.all([
          indexedDBService.userTechniques.getByUserId(userId),
          indexedDBService.techniques.getAll(),
          indexedDBService.positions.getAll()
        ]);

        const joinedData: UserTechnique[] = userTechniques
          .map(userTech => {
            const technique = techniques.find(t => t.id === userTech.technique_id);
            const position = positions.find(p => p.id === technique?.position_id);

            return {
              userTech,
              technique,
              position
            };
          })
          .filter(({ technique }) => technique !== undefined) // Only include user techniques with valid techniques
          .map(({ userTech, technique }) => ({
            id: userTech.id,
            user_id: userTech.user_id,
            technique_id: userTech.technique_id,
            proficiency_level: userTech.proficiency_level || 'beginner',
            is_favorite: Boolean(userTech.is_favorite),
            notes: userTech.notes,
            last_practiced: userTech.last_practiced,
            created_at: userTech.created_at,
            technique: transformTechniqueOfflineToTechnique(technique!)
          }));

        setData(joinedData);
        setIsFromCache(true);
      } catch {
        setData([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, state.isOnline, state.dataLastUpdated, cacheFirst, forceRefresh, maxAge]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh
  };
}

/**
 * Hook for positions with offline support
 *
 * Fetches all BJJ positions with cache-first loading.
 * Positions are relatively static data that rarely changes.
 *
 * @param options - Fetch options
 * @returns All positions data with loading states
 */
export function useOfflinePositions(
  _options: DataFetchOptions = {}
): DataFetchResult<Position[]> {
  const { state } = useOfflineState();
  const [data, setData] = useState<Position[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Ensure IndexedDB is initialized
      await indexedDBService.initialize();

      // Try cache first
      const cachedData = await indexedDBService.positions.getAll();

      // If we have cached data, use it
      if (cachedData.length > 0) {
        setData(cachedData);
        setIsFromCache(true);
        setLastUpdated(new Date().toISOString());
        setLoading(false);
        return;
      }

      // If cache is empty and we're online, fetch from server
      if (navigator.onLine) {
        try {
          const { getPositions } = await import('@/services/positionService');
          const serverPositions = await getPositions();

          // Store in cache for future use
          if (serverPositions && serverPositions.length > 0) {
            // Transform to offline format and store
            const offlinePositions: PositionOffline[] = serverPositions.map(pos => ({
              id: pos.id,
              name: pos.name,
              description: pos.description || '',
              created_at: pos.created_at,
              updated_at: pos.created_at // Use created_at as fallback for updated_at
            }));

            // Store each position individually
            for (const position of offlinePositions) {
              await indexedDBService.positions.update(position.id, position);
            }
            setData(serverPositions);
            setIsFromCache(false);
          }
        } catch (fetchError) {
          console.error('Failed to fetch positions from server:', fetchError);
          // If server fetch fails, use empty array
          setData([]);
        }
      } else {
        // If offline and no cache, return empty array
        setData([]);
      }

      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch positions';
      setError(errorMessage);
      setData([]);
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (state.isOnline) {
      try {
        // Import sync service dynamically to avoid circular dependencies
        const { syncService } = await import('@/services/offline');

        // Only sync if not already syncing to prevent loops
        if (!syncService.getStatus().isSyncing) {
          await syncService.performSync();
        }
      } catch (syncError) {
        console.error('Sync failed during refresh:', syncError);
      }
    }
    await fetchData();
  }, [state.isOnline, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh
  };
}
